/**
 * Reads one part out of a flat OPC `pkg:package`, the single-XML shape of a
 * Word document that Office.js hands the add-in for a range and that the
 * markup document reference carries to the web routes, and writes such a
 * package from the parts of a `.docx` the web opened itself.
 *
 * A `.docx` on disk is a zip of parts; the flat OPC form lists the same parts
 * as `pkg:part` elements, each naming itself with `pkg:name` and wrapping its
 * XML in `pkg:xmlData`. The comments reader and the numbering reader both need
 * a part by name, so the lookup lives here once.
 */
import { PACKAGE_NS } from "./ooxml-names";
import { XmlHost } from "../xml-host";

/**
 * Parses XML through the installed host; null when the parser reports an
 * error, so a caller can treat a fragment or broken markup as "no parts".
 *
 * WHY there are two ways to report an error
 *   The browser's parser answers broken markup with a document holding a
 *   `parsererror` element. xmldom, the server's host, throws a `ParseError`
 *   instead, so without the catch a numbering, styles or comments part it
 *   could not read failed the whole document load on the server rather than
 *   reading as "no parts".
 */
export function parseXmlDocumentOrNull(xml: string): Document | null {
    const xmlParser = XmlHost.domParser();
    let parsed: Document;
    try {
        parsed = xmlParser.parseFromString(xml, "application/xml");
    } catch (parseFailure) {
        /**
         * CASES
         *   - xmldom's ParseError: the markup is broken -> null
         *   - anything else: not a markup problem       -> rethrown
         */
        if (parseFailure instanceof Error && parseFailure.name === "ParseError") return null;
        throw parseFailure;
    }
    /**
     * CASES
     *   - the parser reported an error -> null
     *   - otherwise                    -> the document
     */
    return parsed.getElementsByTagName("parsererror").length > 0 ? null : parsed;
}

/** One part of a package as text: its name as `pkg:name` spells it, and its XML as the part on disk holds it. */
export interface PackagePartText {
    partName: string;
    partXml: string;
}

/**
 * The XML declaration a part on disk may open with, and the byte order mark
 * before it: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`.
 */
const LEADING_XML_DECLARATION = /^﻿?\s*<\?xml[^>]*\?>\s*/;

/**
 * A flat OPC `pkg:package` holding the given parts, the shape the add-in's reads
 * and the markup document reference already have.
 *
 * WHY a `.docx` the web opened is walked as a package
 *   A `w:hyperlink` element names its address only through the relationships
 *   part (`hyperlinkTargetsOf`). Walked as its `word/document.xml` text alone, a
 *   stored or uploaded file read every element link with no address, while the
 *   add-in's own read of the same document had it, so a link instruction the core
 *   wrote from the web's read never met the link the add-in's engine found.
 *
 * WHY the XML declaration is cut
 *   Inside `pkg:xmlData` a declaration is not well-formed XML, so each part keeps
 *   only its root element.
 *
 * EXAMPLE
 *   [{ partName: "/word/document.xml",            partXml: '<?xml version="1.0"?><w:document …>…</w:document>' },
 *    { partName: "/word/_rels/document.xml.rels", partXml: '<Relationships …>…</Relationships>' }]
 *   -> '<pkg:package xmlns:pkg="…"><pkg:part pkg:name="/word/document.xml"><pkg:xmlData><w:document …>…</w:document></pkg:xmlData></pkg:part>
 *       <pkg:part pkg:name="/word/_rels/document.xml.rels"><pkg:xmlData><Relationships …>…</Relationships></pkg:xmlData></pkg:part></pkg:package>'
 */
export function flatOpcPackageOf(parts: PackagePartText[]): string {
    const packageParts = parts.map(({ partName, partXml }) => `<pkg:part pkg:name="${partName}"><pkg:xmlData>${partXml.replace(LEADING_XML_DECLARATION, "")}</pkg:xmlData></pkg:part>`);
    return `<pkg:package xmlns:pkg="${PACKAGE_NS}">${packageParts.join("")}</pkg:package>`;
}

/**
 * The serialised XML of the part named `partName` in a parsed flat OPC package.
 *
 * CASES
 *   - the package holds the part with element content -> that content, serialised
 *   - the part is absent, or holds no element          -> null
 *
 * EXAMPLE
 *   flatOpcPartXml(package, "/word/numbering.xml") -> "<w:numbering …>…</w:numbering>"
 */
export function flatOpcPartXml(packageDocument: Document, partName: string): string | null {
    const partRoot = flatOpcPartRoot(packageDocument, partName);
    /**
     * CASES
     *   - the part has a root element -> that element, serialised
     *   - it is absent or empty       -> null
     */
    return partRoot === null ? null : XmlHost.xmlSerializer().serializeToString(partRoot);
}

/**
 * The root element of the part named `partName` in a parsed flat OPC package, read
 * in place, for a caller that walks the part's elements rather than parsing its
 * serialised XML a second time.
 *
 * NULL
 *   The part is absent, or holds no element.
 *
 * EXAMPLE
 *   flatOpcPartRoot(package, "/word/document.xml") -> the part's `w:document` element
 */
export function flatOpcPartRoot(packageDocument: Document, partName: string): Element | null {
    for (const part of Array.from(packageDocument.getElementsByTagNameNS(PACKAGE_NS, "part"))) {
        const name = part.getAttributeNS(PACKAGE_NS, "name") ?? part.getAttribute("pkg:name");
        /**
         * WHEN this part is not the one asked for
         *   CONSEQUENCE
         *     keep looking.
         */
        if (name !== partName) continue;
        const xmlData = part.getElementsByTagNameNS(PACKAGE_NS, "xmlData")[0];
        /**
         * CASES
         *   - the part wraps its XML in `pkg:xmlData` -> its first element child is the part's root
         *   - the part has no `pkg:xmlData`           -> undefined, handled as absent below
         */
        const root = xmlData === undefined ? undefined : Array.from(xmlData.childNodes).find((node): node is Element => node.nodeType === 1);
        /**
         * WHEN the part has no element content
         *   CONSEQUENCE
         *     treat it as absent.
         */
        return root ?? null;
    }
    return null;
}
